import { isRunningTests } from '@glow/common'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import React, {
  ComponentProps,
  createContext,
  ElementType,
  Fragment,
  HTMLAttributes,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import type { IconProp } from '../icon'
import { IconFa } from '../icon-fa'
import { useModalPortalContainer } from '../modal-base/NativeModalBase'
import { Text } from '../text/Text'
import { Tooltip } from '../tooltip'
import { getContainerEl, getMenuStyles, getScrollParents } from './getMenuStyles'
import styles from './menu.module.css'

type PropsOf<Tag = any> = Tag extends ElementType ? ComponentProps<Tag> : never

interface MenuContext {
  button: {
    ref: RefObject<HTMLElement | null>
    onClick: () => void
    id: string
  }
  items: {
    ref: RefObject<HTMLDivElement | null>
  } & ComponentProps<'div'>
  open: boolean
  openInPortal: boolean
}

const MenuContext = createContext<MenuContext | undefined>(undefined)

export const useMenuContext = () => {
  const context = useContext(MenuContext)

  if (!context) {
    throw new Error('Menu components must be rendered with Menu as a parent')
  }

  return context
}

export interface MenuProps {
  children: ReactNode | ((props: RenderProp) => ReactNode)
  className?: string
  scrollContainerIds?: string[]
  containerId?: string
  offset?: [x: number, y: number]
  placement?: 'top-end'
  positionStrategy?: 'fixed' | 'absolute' // default is absolut
  openInPortal?: boolean
  onToggleMenu?: (open: boolean) => void
  isOpen?: boolean
}

export const Menu = ({
  children,
  scrollContainerIds,
  containerId,
  offset,
  positionStrategy = 'absolute',
  openInPortal = false,
  onToggleMenu,
  isOpen: outerIsOpen,
  className
}: MenuProps) => {
  const buttonElement = useRef<HTMLElement>(null)
  const menuElement = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(outerIsOpen ?? false)
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties | undefined>()
  const [scrollParentsEl, setScrollParentsEl] = useState<HTMLElement[]>([])
  const timeoutRef = useRef<NodeJS.Timeout>(undefined)
  const closeFrameRef = useRef<number>(undefined)

  useEffect(() => {
    if (outerIsOpen !== undefined) setIsOpen(outerIsOpen)
  }, [outerIsOpen])

  const calcAndSetPosStyles = () => {
    // Positioning, Menu should always be displayed bottom right of button if possible, if not possible,
    // make it fit above and left

    if (menuElement.current && buttonElement.current) {
      const containerEl = getContainerEl(containerId)
      const containerRect = containerEl.getBoundingClientRect()
      const menuRect = menuElement.current.getBoundingClientRect()
      const buttonRect = buttonElement.current.getBoundingClientRect()

      setMenuStyles(getMenuStyles(menuRect, buttonRect, containerRect, positionStrategy, offset, openInPortal))
    }
  }

  const closeMenu = () => {
    removeScrollListeners()
    clearTimeout(timeoutRef.current)
    setIsOpen(false)
    onToggleMenu?.(false)
  }

  useEffect(() => {
    return () => {
      closeMenu()
    }
  }, [])

  const setupScrollListeners = () => {
    const scrollParents = getScrollParents(buttonElement.current)

    if (scrollParents.length > 0) {
      setScrollParentsEl(scrollParents)
      scrollParents.forEach((scrollParent) => {
        scrollParent.addEventListener('scroll', closeMenu)
      })
    }
    document.addEventListener('scroll', closeMenu)
    scrollContainerIds?.forEach((scrollContainerId) => {
      if (scrollParents.every((scrollParent) => scrollParent.id !== scrollContainerId)) {
        document.getElementById(scrollContainerId)?.addEventListener('scroll', closeMenu)
      }
    })
  }

  const removeScrollListeners = () => {
    document.removeEventListener('scroll', closeMenu)
    scrollParentsEl.forEach((scrollParent) => {
      scrollParent.removeEventListener('scroll', closeMenu)
    })
    scrollContainerIds?.forEach((scrollContainerId) => {
      document.getElementById(scrollContainerId)?.removeEventListener('scroll', closeMenu)
    })
  }

  const closeIfApplicable = (event: MouseEvent) => {
    if (event.detail === 0) {
      // keypress, handled separately
    } else {
      // mouse event
      if (buttonElement.current && event.composedPath().includes(buttonElement.current)) {
        // dont close if input is selected
      } else if (menuElement.current && buttonElement.current) {
        if (event.composedPath().includes(menuElement.current)) {
          closeFrameRef.current = requestAnimationFrame(() => {
            closeMenu()
          })
        } else if (!event.composedPath().includes(buttonElement.current)) {
          closeMenu()
        }
      }
    }
  }

  const closeOnValueSelectIfApplicable = (event: KeyboardEvent): any => {
    if (
      menuElement.current &&
      event.composedPath().includes(menuElement.current) &&
      (event.key === 'Enter' || event.key === 'Escape')
    ) {
      closeMenu()
    }
  }

  const failedToRenderMenu = () => {
    setMenuStyles({ ...menuStyles, opacity: 1 })
  }

  const openMenu = (retry = 0) => {
    if (isOpen) {
      closeMenu()
      return
    }
    if (retry > 10) {
      console.error('Failed to open ui-components/menu after 100ms')
      failedToRenderMenu()
      return
    }
    if (closeFrameRef.current !== undefined) cancelAnimationFrame(closeFrameRef.current)
    setIsOpen(true)
    onToggleMenu?.(true)
    if (retry === 0) {
      setupScrollListeners()
    }
    setTimeout(() => {
      // After animation, set Menu position
      if (menuElement.current?.clientHeight) {
        calcAndSetPosStyles()
      } else if (isRunningTests()) {
        calcAndSetPosStyles()
      } else {
        openMenu(retry + 1)
      }
    }, 10)
  }

  const menuWrapperId = useId()
  const buttonId = useId()

  useEffect(() => {
    window.addEventListener('click', closeIfApplicable)
    window.addEventListener('keydown', closeOnValueSelectIfApplicable)
    return () => {
      window.removeEventListener('click', closeIfApplicable)
      window.removeEventListener('keydown', closeOnValueSelectIfApplicable)
    }
  }, [closeIfApplicable, closeOnValueSelectIfApplicable])

  const menuContext = useMemo(
    (): MenuContext => ({
      button: {
        ref: buttonElement,
        id: buttonId,
        onClick: openMenu
      },
      items: {
        ref: menuElement,
        style: menuStyles,
        id: menuWrapperId
      },
      open: isOpen,
      openInPortal
    }),
    [buttonElement, menuElement, menuStyles, isOpen, openInPortal]
  )

  return (
    <MenuContext.Provider value={menuContext}>
      {positionStrategy === 'fixed' ? (
        typeof children === 'function' ? (
          children({ open: isOpen })
        ) : (
          children
        )
      ) : (
        <div className={classNames(className, 'relative w-fit')}>
          {typeof children === 'function' ? children({ open: isOpen }) : children}
        </div>
      )}
    </MenuContext.Provider>
  )
}

interface RenderProp {
  open: boolean
}

export interface MenuButtonTagProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  as?: React.ElementType
  children: ReactNode | ((props: RenderProp) => React.ReactElement)
}

export const MenuButton = ({ as = 'button', children, className, onClick, ...other }: MenuButtonTagProps) => {
  const { open, items, button } = useMenuContext()
  const { onClick: internalButtonClick, ...buttonProps } = button
  const Container = as

  const handleClick = (event: Event) => {
    internalButtonClick()
    onClick?.(event as any)
  }

  return (
    <Container
      aria-haspopup="menu"
      type="button"
      aria-expanded={open}
      aria-controls={items.id}
      className={classNames(styles.button, className)}
      onClick={handleClick}
      {...buttonProps}
      {...other}
    >
      {typeof children === 'function' ? children({ open }) : children}
    </Container>
  )
}

type MenuItemsProps<Tag> = {
  as?: Tag
  children: ReactNode
  containerClassName?: string
} & PropsOf<Tag>

export const MenuItems = <Tag extends React.ElementType<any> = 'div'>({
  children,
  as = 'div',
  ...props
}: MenuItemsProps<Tag>) => {
  const context = useMenuContext()
  const portalContainer = useModalPortalContainer()

  if (context.openInPortal) {
    return context.open ? (
      createPortal(
        <MenuItemsInternal as={as} {...props}>
          {children}
        </MenuItemsInternal>,
        portalContainer
      )
    ) : (
      <div />
    )
  }

  return context.open ? (
    <MenuItemsInternal as={as} {...props}>
      {children}
    </MenuItemsInternal>
  ) : (
    <div />
  )
}

const MenuItemsInternal = <Tag extends React.ElementType<any> = 'div'>({
  as = 'div',
  children,
  className,
  containerClassName,
  ...props
}: MenuItemsProps<Tag>) => {
  const context = useMenuContext()

  const Container = as
  return (
    <div
      ref={context.items.ref}
      style={context.items.style}
      className={classNames(containerClassName, styles['items-container'])}
    >
      <Transition
        as={Fragment}
        show={context.open}
        enter="transition ease-out duration-20"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Container
          role="menu"
          id={context.items.id}
          aria-labelledby={context.button.id}
          className={classNames(styles.items, className)}
          {...props}
        >
          {children}
        </Container>
      </Transition>
    </div>
  )
}

export const Header = ({ text, className, ...other }: React.HTMLAttributes<HTMLDivElement> & { text: string }) => {
  return (
    <div className={classNames(styles.header, className)} {...other}>
      <Text as="span" textType="xs">
        {text}
      </Text>
    </div>
  )
}

type MenuItemProps<Tag> = {
  // as can be used to provide a custom element as the wrapper element, useful when for example
  // integrating with react-router links
  as?: Tag
  icon?: IconProp
  children: ReactNode
  disabled?: boolean
} & PropsOf<Tag>

export const MenuItem = <Tag extends React.ElementType<any> = 'button'>({
  as = 'button',
  icon,
  children,
  className,
  ...other
}: MenuItemProps<Tag>) => {
  const Container = as

  return (
    <Container role="menuitem" className={classNames(styles.item, 'hover-aa-supported', className)} {...other}>
      {icon && <IconFa icon={icon} className={styles['item-icon']} />}
      <Text as="span" textType="sm" className="text-emphasis-high">
        {children}
      </Text>
    </Container>
  )
}

type MenuItemWithToolTip<Tag> = {
  tooltipText: string
} & MenuItemProps<Tag>

export const MenuItemWithToolTip = <Tag extends React.ElementType<any> = 'button'>({
  tooltipText,
  as = 'button',
  children,
  ...other
}: MenuItemWithToolTip<Tag>) => {
  const tooltipId = useId()

  return (
    <span className="flex items-center">
      <MenuItem as={as} {...other}>
        {children}
      </MenuItem>
      <span className={`-ml-1 ${other.disabled ? 'opacity-30' : 'invisible'}`}>
        <Tooltip referenceId={tooltipId} label={tooltipText}>
          <IconFa icon={['far', 'info-circle']} id={tooltipId} />
        </Tooltip>
      </span>
    </span>
  )
}
